import { Link } from 'react-router-dom';
import { NavBar } from '../../components/NavBar';
import { SharedFooter } from '../../components/SharedFooter';

const lastUpdated = 'May 20, 2026';

const dataCategories = [
  {
    title: 'Account and profile data',
    body: 'Name, display name, username, email address, login identifiers, profile text, country, language preferences, role information, model/host/artist settings, public profile choices, and account status.',
  },
  {
    title: 'Model application and directory data',
    body: 'Application answers, modelling styles, nude/portraiture preferences, camera equipment, posing-space photos, profile photos, high-resolution model photos, admin review notes, approval status, and related communications.',
  },
  {
    title: 'Event and session data',
    body: 'Event details, ticket and invite status, participant names, roles, attendance, session chat, uploaded artwork, pose schedule information, camera and microphone state, LiveKit participant identifiers, connection diagnostics, and moderation records.',
  },
  {
    title: 'Recordings and reference-pack media',
    body: 'Video, audio, still captures, source recording chunks, pose names, pose timing, camera labels, file metadata, selected/rejected media, access settings, purchase access periods, buyer notes, and deletion or withdrawal requests.',
  },
  {
    title: 'Job board and messaging data',
    body: 'Model job listings, applications, short notes, host selections, invitation messages, acceptance/rejection communications, and notifications sent through the platform.',
  },
  {
    title: 'Payment and payout data',
    body: 'Amounts, currencies, Stripe customer and connected-account identifiers, checkout session identifiers, transaction status, refunds, disputes, payout status, card brand or last-four information when provided by Stripe, and accounting records.',
  },
  {
    title: 'Device, usage, and technical data',
    body: 'IP address, browser and device information, log data, upload progress, local storage state, camera and microphone device preferences, camera device keys/labels, error reports, performance diagnostics, security events, and authentication/session data.',
  },
];

const purposeRows = [
  {
    purpose: 'Provide the platform',
    examples: 'Create accounts, run sessions, stream video, manage events, upload media, sell packs, process purchases, show purchased content, and send service notifications.',
    basis: 'Contract and legitimate interests.',
  },
  {
    purpose: 'Model approval, safety, and trust',
    examples: 'Review model applications, manage directory visibility, assess job-board eligibility, handle reports, remove unsafe content, and protect models from misuse of likeness or media.',
    basis: 'Contract, legitimate interests, consent where appropriate, and legal obligations where required.',
  },
  {
    purpose: 'Payments and payouts',
    examples: 'Create checkouts, verify payout eligibility, process refunds, settle host/model revenue, handle chargebacks, maintain transaction records, and prevent payment abuse.',
    basis: 'Contract, legal obligation, and legitimate interests.',
  },
  {
    purpose: 'Communications',
    examples: 'Send confirmations, calendar details, model invitations, job application updates, reference-pack access decisions, support replies, and administrative notices.',
    basis: 'Contract and legitimate interests. Consent where required for optional marketing.',
  },
  {
    purpose: 'Security, diagnostics, and improvement',
    examples: 'Debug streams/uploads, investigate unauthorized access, maintain service reliability, prevent abuse, improve recording quality, and understand feature usage.',
    basis: 'Legitimate interests and legal obligations.',
  },
];

const userRights = [
  'Access a copy of personal data we hold about you.',
  'Ask us to correct inaccurate or incomplete data.',
  'Ask us to delete data where we no longer need it or where deletion is legally required.',
  'Ask us to restrict or object to certain processing.',
  'Ask for a portable copy of data you provided to us, where the right applies.',
  'Withdraw consent where processing is based on consent.',
  'Complain to a data-protection authority. In the UK, that is the Information Commissioner\'s Office.',
];

const retentionItems = [
  'Account and profile records are kept while your account is active and for a reasonable period afterwards for safety, support, dispute, and audit reasons.',
  'Payment, payout, refund, chargeback, tax, and accounting records may be kept for the period required by law or payment-provider rules, often at least six years.',
  'Reference-pack media remains available while needed for the event, pack, purchase, access period, support, disputes, or model/seller records, unless deleted or withdrawn earlier.',
  'Source recording chunks and queued uploads may temporarily remain in browser storage until upload completes, fails, is retried, or local browser storage is cleared.',
  'Security logs, diagnostics, and abuse-prevention records are kept only as long as reasonably needed for reliability, safety, investigation, and legal protection.',
];

const researchLinks = [
  {
    label: 'ICO privacy notice checklist',
    href: 'https://ico.org.uk/media/for-organisations/documents/1625126/privacy-notice-checklist.pdf',
  },
  {
    label: 'ICO lawful basis guidance',
    href: 'https://ico.org.uk/for-organisations/uk-gdpr-guidance-and-resources/lawful-basis/a-guide-to-lawful-basis/',
  },
  {
    label: 'ICO individual rights guidance',
    href: 'https://ico.org.uk/for-organisations/uk-gdpr-guidance-and-resources/individual-rights/individual-rights/',
  },
  {
    label: 'ICO special category and biometric data guidance',
    href: 'https://ico.org.uk/for-organisations/uk-gdpr-guidance-and-resources/lawful-basis/special-category-data/what-is-special-category-data/',
  },
  {
    label: 'ICO age appropriate design code',
    href: 'https://ico.org.uk/for-organisations/uk-gdpr-guidance-and-resources/childrens-information/childrens-code-guidance-and-resources/age-appropriate-design-a-code-of-practice-for-online-services/',
  },
  {
    label: 'Stripe Connected Account Agreement',
    href: 'https://stripe.com/legal/connect-account',
  },
];

export function PrivacyPage() {
  return (
    <div className="min-h-screen bg-bg pt-[88px]">
      <NavBar variant="light" />
      <main className="page-container py-12">
        <div className="mb-10">
          <div className="page-eyebrow">Privacy</div>
          <h1 className="page-heading">LifeDrawing.live privacy notice</h1>
          <p className="mt-4 max-w-3xl text-[17px] leading-8 text-text-dim">
            LifeDrawing.live handles live video, model likeness, applications, bookings, payments, recordings,
            reference packs, and artwork. This notice explains what personal data we collect, why we use it,
            who we share it with, and the choices and rights available to you.
          </p>
          <p className="mt-3 text-sm text-text-faint">Last updated: {lastUpdated}</p>
          <div className="mt-5 flex flex-wrap gap-3 text-sm">
            <Link to="/terms" className="border-b border-line pb-px text-text-dim hover:border-text hover:text-text">
              Terms
            </Link>
            <Link to="/reference-pack-licenses" className="border-b border-line pb-px text-text-dim hover:border-text hover:text-text">
              Reference pack licenses
            </Link>
          </div>
        </div>

        <section className="mb-10 rounded-xl border border-line bg-panel p-6 shadow-sm">
          <h2 className="font-display text-2xl font-normal text-text">Who is responsible for your data.</h2>
          <div className="mt-4 max-w-3xl space-y-3 text-sm leading-7 text-text-dim">
            <p>
              LifeDrawing.live is responsible for the platform data we use to provide accounts, sessions,
              model applications, reference packs, job board features, payments, support, safety, and
              administration.
            </p>
            <p>
              Hosts, models, artists, and buyers may also receive personal data through the platform, for example
              attendee names, model profile information, application notes, chat messages, or purchased reference
              access. They are responsible for using that data lawfully and respectfully.
            </p>
            <p>
              For privacy requests, contact <a className="border-b border-line pb-px hover:border-text hover:text-text" href="mailto:events@lifedrawing.live">events@lifedrawing.live</a>.
            </p>
          </div>
        </section>

        <section className="mb-10">
          <h2 className="font-display text-2xl font-normal text-text">Personal data we collect.</h2>
          <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
            {dataCategories.map((item) => (
              <article key={item.title} className="rounded-lg border border-line bg-white p-4">
                <h3 className="font-medium text-text">{item.title}</h3>
                <p className="mt-2 text-sm leading-6 text-text-dim">{item.body}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="mb-10 rounded-xl border border-line bg-white p-5">
          <h2 className="font-display text-2xl font-normal text-text">Sensitive model media and likeness.</h2>
          <div className="mt-3 space-y-3 text-sm leading-7 text-text-dim">
            <p>
              Model photos, live video, nude or semi-nude modelling preferences, posing-space images, recordings,
              and reference packs can be sensitive even when they are not legally classified as special category
              data. We treat them as high-trust content.
            </p>
            <p>
              We do not use model media for facial recognition, biometric identification, or AI model training.
              We also prohibit buyers and users from using reference packs, screenshots, downloads, or source
              media for those purposes.
            </p>
            <p>
              Digital photographs are not automatically biometric data under UK data-protection guidance, but
              they can become biometric data if processed through specific techniques to uniquely identify a
              person. That is not part of the LifeDrawing.live product.
            </p>
          </div>
        </section>

        <section className="mb-10">
          <h2 className="font-display text-2xl font-normal text-text">Why we use personal data.</h2>
          <div className="mt-4 overflow-hidden rounded-xl border border-line bg-white">
            <div className="grid grid-cols-1 divide-y divide-line-soft">
              {purposeRows.map((row) => (
                <div key={row.purpose} className="grid grid-cols-1 gap-3 p-4 md:grid-cols-[180px_minmax(0,1fr)_220px]">
                  <div className="font-medium text-text">{row.purpose}</div>
                  <div className="text-sm leading-6 text-text-dim">{row.examples}</div>
                  <div className="text-sm leading-6 text-text-faint">{row.basis}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="mb-10 grid grid-cols-1 gap-4 lg:grid-cols-2">
          <article className="rounded-xl border border-line bg-white p-5">
            <h2 className="font-display text-2xl font-normal text-text">Who we share data with.</h2>
            <div className="mt-3 space-y-3 text-sm leading-7 text-text-dim">
              <p>
                We share data with service providers that help run the platform, including authentication,
                payments and payouts, email delivery, hosting, file storage, video infrastructure, analytics or
                diagnostics, support, and security.
              </p>
              <p>
                We use providers such as Stripe for payments and payouts, Postmark for email, LiveKit for live
                video, and storage/hosting providers for uploaded files and recordings. Authentication is handled
                by our own platform.
                These providers process data under their own terms and privacy policies where they act as
                independent providers, especially for payments and identity verification.
              </p>
              <p>
                We may share data with hosts, models, artists, buyers, administrators, payment providers, legal
                advisers, insurers, regulators, or law enforcement where needed to provide the service, complete a
                transaction, resolve a dispute, protect safety, enforce terms, or comply with law.
              </p>
            </div>
          </article>

          <article className="rounded-xl border border-line bg-white p-5">
            <h2 className="font-display text-2xl font-normal text-text">Cookies and local storage.</h2>
            <div className="mt-3 space-y-3 text-sm leading-7 text-text-dim">
              <p>
                We use cookies, local storage, and similar browser storage for sign-in, session participation,
                role and room state, upload queues, camera and microphone preferences, progress tracking, and basic reliability.
              </p>
              <p>
                Some media may be stored temporarily in your browser while uploads finish, especially after a
                session when model camera videos or high-resolution stills are being uploaded to a reference pack.
                Clearing browser storage may remove queued local uploads before they reach the server.
              </p>
              <p>
                We do not currently describe the platform as an advertising-tracking product. If that changes, the
                privacy notice should be updated before those tools are used.
              </p>
            </div>
          </article>
        </section>

        <section className="mb-10 grid grid-cols-1 gap-4 lg:grid-cols-2">
          <article className="rounded-xl border border-line bg-white p-5">
            <h2 className="font-display text-2xl font-normal text-text">How long we keep data.</h2>
            <ul className="mt-3 space-y-3 text-sm leading-7 text-text-dim">
              {retentionItems.map((item) => (
                <li key={item} className="flex gap-2">
                  <span className="mt-[0.7em] h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </article>

          <article className="rounded-xl border border-line bg-white p-5">
            <h2 className="font-display text-2xl font-normal text-text">International processing and security.</h2>
            <div className="mt-3 space-y-3 text-sm leading-7 text-text-dim">
              <p>
                Some providers may process data outside your country, including outside the UK or EEA. Where
                required, we use appropriate safeguards such as contracts, provider transfer mechanisms, and
                security controls.
              </p>
              <p>
                We use technical and organisational measures such as HTTPS, authenticated access, permission
                checks, limited admin access, signed media URLs, logging, and provider security controls. No
                internet service can be guaranteed to be perfectly secure.
              </p>
            </div>
          </article>
        </section>

        <section className="mb-10 rounded-xl border border-line bg-white p-5">
          <h2 className="font-display text-2xl font-normal text-text">Your privacy rights.</h2>
          <p className="mt-3 max-w-3xl text-sm leading-7 text-text-dim">
            Depending on where you live and the processing involved, you may have the following rights. We may need
            to verify your identity before acting on a request, and some rights may be limited where we need to keep
            records for safety, legal, payment, tax, dispute, or fraud-prevention reasons.
          </p>
          <ul className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
            {userRights.map((right) => (
              <li key={right} className="rounded-lg border border-line-soft bg-bg-secondary p-3 text-sm leading-6 text-text-dim">
                {right}
              </li>
            ))}
          </ul>
        </section>

        <section className="mb-10 rounded-xl border border-line bg-white p-5">
          <h2 className="font-display text-2xl font-normal text-text">Children.</h2>
          <p className="mt-3 max-w-3xl text-sm leading-7 text-text-dim">
            LifeDrawing.live is not for children. You must be at least 18 to use the platform. If you believe a
            child has provided data, appeared in a session, been depicted in uploaded content, or been included in
            reference media, contact us immediately at events@lifedrawing.live.
          </p>
        </section>

        <section className="rounded-xl border border-line bg-bg-secondary p-6">
          <h2 className="font-display text-xl font-normal text-text">Research basis.</h2>
          <p className="mt-2 max-w-3xl text-sm leading-6 text-text-dim">
            This notice is structured around privacy-notice transparency, lawful basis, user rights, children's
            online services, payment-provider relationships, and the specific sensitivity of model images and
            recordings.
          </p>
          <div className="mt-4 flex flex-wrap gap-3">
            {researchLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                target="_blank"
                rel="noreferrer"
                className="rounded border border-line bg-white px-3 py-2 text-sm text-text-dim hover:border-text/40 hover:text-text"
              >
                {link.label}
              </a>
            ))}
          </div>
        </section>
      </main>
      <SharedFooter />
    </div>
  );
}
